import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";
import clsx from "clsx";

import { formatFullDateTime } from "../../libs/dateFormat";
import useErrors from "../../libs/useErrors";
import { getLocalValue, setLocalValue } from "../../libs/localStorage";
import { isArrayEmpty } from "../../libs/common";
import { safeReplaceAll } from "../../libs/replace";

import { KeyValue } from "../../types/keyValue";
import { Reason } from "../../types/location";
import {
  IncommodityCategory,
  getDefaultIncommodityCategoryList,
  IncommodityCreateRequest,
  getIncommodityCategoryList,
  IncommodityCategoryListRequest,
  insertIncommodity,
} from "../../services/centerServices";
import {
  BuildingInfoResponse,
  BuildingFloorItem,
  getBuildingInfo,
  NearBuildingItem,
} from "../../services/buildingServices";
import { useUserContext } from "../../contexts/userContext";

import useSimplePopup from "../../hooks/useSimplePopup";
import useFullScreenLoading from "../../hooks/useFullScreenLoading";

import CenterHeader from "../../components/Center/Header";
import StickyHeader from "../../components/Layout/StickyHeader";
import ProgressBar from "../../components/ProgressBar";
import CommonInput from "../../components/Input/CommonInput";
import PrimaryButton from "../../components/Button/Primary";
import CategoryList from "../../components/Center/CategoryList";
import CommonTextArea from "../../components/TextArea";
import CommonLabel from "../../components/Label";
import CommonImageInput from "../../components/Input/ImageUpload";
import CommonDatetimeInput from "../../components/Input/Datetime";
import CommonPopup from "../../components/Popup";
import CommonButton from "../../components/Button";
import ConfirmReport from "../../components/Center/ConfirmReport";
import RadioButtonGroup from "../../components/Input/RadioButtonGroup";
import BuildingFloorSelector from "../../components/BuildingFloorSelector";
import SimplePopup from "../../components/Popup/Simple";
import BottomGroup from "../../components/Button/Group";
import NeedCertCompanyPopup from "../../components/Popup/NeedCertCompany";
import HorizontalRule from "../../components/Rule/Horizontal";

import ZoomSearchIcon from "../../svgs/icons/ZoomSearchIcon";
import BlueCheck from "../../assets/img/blue-check.png";

type Props = { testStep?: number };

// eslint-disable-next-line no-empty-pattern
export default function Center({ testStep }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user] = useUserContext();
  const [step, setStep] = useState<number>(testStep ?? 0);
  const [defaultCategoryList, setDefaultCategoryList] = useState<
    IncommodityCategory[]
  >([]);
  const [selectedCategory, setSelectedCategory] =
    useState<IncommodityCategory | null>(null);
  const [incommodityCreateRequest, setIncommodityCreateRequest] = useState<
    Partial<IncommodityCreateRequest>
  >({});
  const [keyword, setKeyword] = useState<string>("");
  const [search, setSearch] = useState<boolean>(false);
  const [scheduleDatetime, setScheduleDatetime] = useState<Date | undefined>(
    undefined
  );
  const [popup, setPopup] = useState<
    "confirmation" | "invalidService" | "needCertCompany" | ""
  >("");
  const [simplePopup, setSimplePopup] = useState<"reportFailed" | "">("");
  const [images, setImages] = useState<(File | string)[]>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<NearBuildingItem>();
  const [selectedFloor, setSelectedFloor] = useState<BuildingFloorItem>();
  const [requestTypeItems, setRequestTypeItems] = useState<KeyValue[]>([]);
  const [selectedRequestTypeItem, setSelectedRequestTypeItem] =
    useState<KeyValue>();
  const [createdIncommodityId, setCreatedIncommodityId] = useState<string>();
  const [buildingInfo, setBuildingInfo] = useState<BuildingInfoResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingSelector, setIsLoadingSelector] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({ isLoading });

  const [errors, { removeError, setError }] = useErrors();
  const buildingSelectRef = React.useRef<HTMLInputElement>(null);
  const floorSelectRef = React.useRef<HTMLInputElement>(null);
  const locationRef = React.useRef<HTMLTextAreaElement>(null);
  const descriptionRef = React.useRef<HTMLTextAreaElement>(null);
  const deadLineRef = React.useRef<HTMLInputElement>(null);
  const scheduleOption = [
    { key: "asap", value: "즉시 신청" },
    { key: "request", value: "예약" },
  ];
  const [scheduleMode, setScheduleMode] = useState<KeyValue>(scheduleOption[0]);
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const firstApproach = useRef(false);
  const [recentCategoryList, setRecentCategoryList] = useState<
    IncommodityCategory[]
  >([]);
  const reason = Reason.REPORT;

  const handleInsertIncommodity = async () => {
    setIsLoading(true);
    const formData = new FormData();
    images.forEach((image) => formData.append("uploadFiles", image));
    formData.append(
      "incommodityCreateDto",
      JSON.stringify({
        serviceTemplateId: selectedCategory?.serviceTemplateId,
        ...selectedBuilding,
        ...selectedFloor,
        requestDetail: selectedRequestTypeItem?.key,
        ...incommodityCreateRequest,
        deadLine:
          scheduleMode.key === "asap"
            ? null
            : formatFullDateTime(scheduleDatetime as Date),
      })
    );
    try {
      const { status, data } = await insertIncommodity(formData);
      if (status === HttpStatusCode.Ok) {
        if (data.centerIncommodityId !== 0) {
          getBuildingInfo({
            buildingId: selectedBuilding?.buildingId ?? "",
          }).then((res) => {
            setBuildingInfo(res.data);
          });

          await setPopup("");
          setCreatedIncommodityId(data.centerIncommodityId.toString());
          setStep((prev) => prev + 1);
        } else {
          open("생성된 불편신고 정보가 없습니다.");
        }
      } else {
        open("불편신고 생성 중 오류가 발생했습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        setSimplePopup("reportFailed");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const searchCategoryList = async (text: string) => {
    try {
      const { status, data } = await getIncommodityCategoryList({
        buildingId: selectedBuilding?.buildingId,
        searchWord: text,
      } as IncommodityCategoryListRequest);
      if (status === HttpStatusCode.Ok) {
        const newCategoryList: IncommodityCategory[] = [];
        Object.values(data).forEach((categoryList) =>
          newCategoryList.push(...categoryList)
        );
        setDefaultCategoryList(newCategoryList);
        setSearch(true);
      }
    } catch (e: any) {
      console.log(e);
    }
  };

  const clearAllError = () => {
    removeError("buildingSelect");
    removeError("floorSelect");
    removeError("location");
    removeError("description");
    removeError("deadLine");
  };

  const isDisabled = (error?: boolean) => {
    if (step === 0) {
      if (!selectedBuilding) {
        if (error) {
          buildingSelectRef.current?.focus();
          setError("buildingSelect", "건물을 선택해주세요");
        }
        return true;
      } else {
        removeError("buildingSelect");
      }

      if (!selectedFloor) {
        if (error) {
          floorSelectRef.current?.focus();
          setError("floorSelect", "층을 선택해주세요");
        }
        return true;
      } else {
        removeError("floorSelect");
        return false;
      }
    } else if (step === 2) {
      if (
        selectedCategory?.isRequiredVocPlace === true &&
        (!incommodityCreateRequest.location ||
          incommodityCreateRequest.location.length === 0)
      ) {
        if (error) {
          locationRef.current?.focus();
          setError("location", "요청 장소를 입력해주세요");
        }
        return true;
      }

      if (
        selectedCategory?.isRequiredVocDetail === true &&
        (!incommodityCreateRequest.description ||
          incommodityCreateRequest.description.length === 0)
      ) {
        if (error) {
          descriptionRef.current?.focus();
          setError("description", "상세 내용을 입력해주세요");
        }
        return true;
      }

      if (
        scheduleMode.key === "request" &&
        (!incommodityCreateRequest.deadLine ||
          incommodityCreateRequest.deadLine.length === 0)
      ) {
        if (error) {
          deadLineRef.current?.focus();
          setError("deadLine", "요청 날짜를 선택해 주세요");
        }
        return true;
      }
    }
    clearAllError();
    return false;
  };

  const getRequestKey = (value: string) => {
    switch (value) {
      case "더워요":
        return "HOT";
      case "추워요":
        return "COLD";
      case "환기해주세요":
        return "CHANGEAIR";
      case "신규/변경":
        return "TEL_NEW";
      case "고장/장애":
        return "TEL_HINDRANCE";
      default:
        console.error("관리되지않은 요청 값이 리스트에 있습니다.", value);
        return "UNKNOWN";
    }
    // , LIGHT_ON, LIGHT_OFF;
  };

  const getRecentCategoryList = (category?: IncommodityCategory) => {
    const recentCategoryString = getLocalValue("recentCategory");
    const recentCategory = recentCategoryString
      ? JSON.parse(recentCategoryString)
      : [];

    const filteredRecentCategory = [
      ...(category ? [category] : []),
      ...recentCategory,
    ];

    setRecentCategoryList(
      filteredRecentCategory
        .filter((item: IncommodityCategory, i: number) => {
          return (
            filteredRecentCategory.findIndex((item2: IncommodityCategory) =>
              item.serviceTemplateId && item2.serviceTemplateId
                ? item.serviceTemplateId === item2.serviceTemplateId
                : item.serviceName === item2.serviceName
            ) === i
          );
        })
        .slice(0, 10)
    );

    if (category) {
      setLocalValue("recentCategory", JSON.stringify(filteredRecentCategory));
    }
  };

  const handleSelectCategory = (category: IncommodityCategory) => {
    setSelectedCategory(category);
    setIncommodityCreateRequest((prev) => ({
      ...prev,
      centerCategoryId: category.serviceTemplateId,
    }));
    if (
      category.vocRequestTypeArray &&
      category.vocRequestTypeArray.length > 0
    ) {
      const categoryList = category.vocRequestTypeArray
        .split(",")
        .map((value) => ({ key: getRequestKey(value), value }));
      setRequestTypeItems(categoryList);

      const selectedCategory =
        categoryList.find(
          (category) => category.key === location?.state?.selectedItemKey
        ) ?? categoryList[0];
      setSelectedRequestTypeItem(selectedCategory);
    }

    getRecentCategoryList(category);
  };

  const handleSelectCategoryWithStep = (category: IncommodityCategory) => {
    handleSelectCategory(category);
    setStep((prev) => prev + 1);
  };

  const handleDatetimeChange = (value: Date | undefined) => {
    setIncommodityCreateRequest((prev) => ({
      ...prev,
      deadLine: scheduleDatetime?.toLocaleString(),
    }));
    setScheduleDatetime(value);
  };

  useEffect(() => {
    handleDatetimeChange(scheduleDatetime);
  }, [scheduleDatetime]);

  const handleConfirm = () => {
    if (isDisabled(true)) {
      return;
    }

    if (step === 0) {
      setStep((prev) => prev + 1);

      // 외부에서 serviceTemplateId가 설정된 상태로 진입한 경우, 카테고리가 존재하는 경우에는 건물 선택 시 바로 2스탭으로 이동
      if (location?.state?.serviceTemplateId && !firstApproach.current) {
        const selectedCategory = defaultCategoryList.find(
          (item) =>
            item.serviceTemplateId === location?.state?.serviceTemplateId
        );
        firstApproach.current = true;
        if (selectedCategory) {
          handleSelectCategoryWithStep(selectedCategory);
        }
      }
    } else if (step === 1) {
      setStep((prev) => prev + 1);
      getRecentCategoryList();
    } else if (step === 2) {
      setPopup("confirmation");
    } else {
      setStep((prev) => prev + 1);
    }
  };

  useEffect(() => {
    const getDefaultCategoryListWrapper = async () => {
      if (selectedBuilding?.buildingId) {
        const { status, data } = await getDefaultIncommodityCategoryList({
          buildingId: selectedBuilding?.buildingId ?? "",
        });
        if (status === HttpStatusCode.Ok) {
          setDefaultCategoryList(data);
          setSearch(false);
        }
      }
    };

    if (
      selectedBuilding?.buildingId === location.state?.buildingId &&
      defaultCategoryList.length > 0
    )
      return;
    getDefaultCategoryListWrapper();
  }, [selectedBuilding]);

  useEffect(() => {}, [defaultCategoryList]);

  useEffect(() => {
    if (
      [user.isLoading, isLoading, isLoadingSelector].every(
        (isLoading) => isLoading === false
      )
    ) {
      if (!user.companyId) {
        setPopup("needCertCompany");
      }
    }
  }, [user.isLoading, isLoading, isLoadingSelector]);

  useEffect(() => {
    getRecentCategoryList();
  }, [defaultCategoryList]);

  useEffect(() => {
    if (defaultCategoryList.length <= 0) return;
    if (firstApproach.current) return;

    let skipStep = 0;

    if (location.state?.buildingId && location.state?.buildingFloorId) {
      setSelectedBuilding({
        buildingName: location.state?.buildingName,
        buildingId: location.state?.buildingId,
      } as NearBuildingItem);
      setSelectedFloor({
        floorName: location.state?.buildingFloorName,
        buildingFloorId: location.state?.buildingFloorId,
      } as BuildingFloorItem);
      skipStep = 1;
    }

    if (location.state?.serviceTemplateId) {
      const category = defaultCategoryList.find(
        (item) => item.serviceTemplateId === location.state.serviceTemplateId
      );
      if (category) {
        handleSelectCategory(category);
        skipStep = 2;
      } else {
        // TODO: 원하는 서비스 템플릿이 없다는 에러 필요
      }
    }

    firstApproach.current = true;
    setStep(skipStep);
  }, [
    location.state?.buildingId,
    location.state?.buildingFloorId,
    defaultCategoryList,
  ]);

  if (step === 3) {
    return (
      <>
        <div className="min-h-content bg-white relative px-5 overflow-auto h-fit">
          <StickyHeader
            title="접수 완료"
            onBackClick={() => {
              navigate(location?.state?.parentUrl ?? "/", {
                state: { ...location?.state?.parentState },
              });
            }}
          />

          <div className="flex flex-col justify-between min-h-content h-fit pb-[60px]">
            <div className="flex flex-col gap-[30px]">
              <div className="flex flex-col py-5 gap-2.5">
                <div className="flex flex-col p-5 gap-2.5 justify-center items-center">
                  <div className="text-[26px] font-medium leading-[48px] tracking-[-1.04px]">
                    {selectedCategory?.serviceName}
                  </div>
                  <div className=" bg-brand-category_tag px-2 py-1 rounded w-fit flex items-center gap-1.5 ">
                    <div className="w-1 h-1 rounded-full bg-white"></div>
                    {selectedCategory?.serviceItemTypeName}
                  </div>
                </div>
                <HorizontalRule margin="0" />
              </div>
              <div className="flex flex-col">
                <div className="flex flex-col py-2.5 justify-center items-center">
                  <img src={BlueCheck} alt="complete_icon" className="h-20" />
                  <div className="flex flex-col pt-[25px] gap-2.5 justify-center items-center">
                    <div className="text-2xl font-semibold leading-[140%]">
                      신청 완료
                    </div>
                    <div className="text-sm font-normal text-brand-neutral-60 text-center leading-[18px] whitespace-pre-line">
                      {
                        "샌디매니저가 접수 과정에서 추가 정보가 필요할 경우\n휴대폰으로 연락드릴 수도 있어요\n※ 안심번호로 연결되어 개인정보를 노출하지 않습니다"
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col py-5">
              <div className="text-center text-sm font-normal bg-[#F7F7F7] text-brand-neutral-80 px-2.5 py-5">
                업무시간(18시) 이후에 신청된 건은
                <br />
                익일 업무시간에 처리가 되어 피드백이 늦을 수 있어요
                <br />
                긴급한 경우 아래의 “콜 센터 연결＂을 눌러주세요
              </div>
            </div>
          </div>
        </div>
        <BottomGroup
          className="flex gap-1.5 w-full pb-5 p-5 h-[90px]"
          zIndex="middle"
        >
          {/* {buildingInfo?.helpDeskTel && ( */}
          <CommonButton
            variant="tertiary"
            className=" text-brand-neutral-60 w-full h-bottom-button"
            onClick={() =>
              (window.location.href = "tel:" + buildingInfo?.helpDeskTel ?? "")
            }
          >
            콜센터 연결
          </CommonButton>
          {/* )} */}
          <CommonButton
            className="p-4 text-brand-neutral-60 w-full h-bottom-button"
            variant="tertiary"
            onClick={() =>
              navigate(`/history/incommodity/${createdIncommodityId}`)
            }
          >
            접수 내역 확인
          </CommonButton>
        </BottomGroup>
      </>
    );
  }

  return (
    <div className="min-h-content bg-white relative flex flex-col gap-3">
      {loadingComponent}
      <div className="px-4">
        <StickyHeader
          title="접수하기"
          onBackClick={() => {
            if (step > 0) {
              if (step === 2) {
                setSelectedRequestTypeItem(undefined);
                setRequestTypeItems([]);
              }
              setStep((prev) => prev - 1);
            } else {
              navigate(location?.state?.parentUrl ?? "/", {
                state: { ...location?.state?.parentState },
              });
            }
            setKeyword("");
            clearAllError();
            setIncommodityCreateRequest({});
            setImages([]);
            setScheduleMode(scheduleOption[0]);
            setScheduleDatetime(undefined);
          }}
        />
        <ProgressBar currentStep={step + 1} />
        {step === 2 && (
          <div
            className={clsx([
              "flex justify-between sticky top-[72px] bg-white",
              { "opacity-0": step !== 2, "opacity-100": step === 2 },
            ])}
          >
            <div className="text-base text-brand-neutral-40 leading-[19px]">
              접수하신 서비스
            </div>
            <div className=" text-transparent bg-brand-primary-magenta bg-clip-text font-bold leading-[19px]">
              {selectedCategory?.serviceName}
            </div>
          </div>
        )}
        <div className="pt-10">
          {step === 0 && (
            <>
              <CenterHeader subtitle="샌디가 불편신고를 접수해드릴게요">
                도움이 필요하신
                <br />
                <span className="font-medium">장소</span>를 골라주세요
              </CenterHeader>
              <BuildingFloorSelector
                selectedBuilding={selectedBuilding}
                selectedFloor={selectedFloor}
                onChangeBuilding={(building) => setSelectedBuilding(building)}
                onChangeFloor={(floor) => setSelectedFloor(floor)}
                onClickBuilding={() => removeError("buildingSelect")}
                onClickFloor={() => {
                  removeError("floorSelect");
                }}
                buildingError={errors["buildingSelect"]}
                floorError={errors["floorSelect"]}
                buildingRef={buildingSelectRef}
                floorRef={floorSelectRef}
                onLoaded={() => setIsLoadingSelector(false)}
                onBackHistory={() => {
                  navigate(location?.state?.parentUrl ?? "/");
                }}
                reason={reason}
                isVoc={true}
              />
            </>
          )}
          {step === 1 && (
            <div className="flex flex-col justify-between h-full pb-5">
              <div className="flex flex-col">
                <CenterHeader>
                  <span className="font-medium">무엇</span>을<br />
                  도와드릴까요?
                </CenterHeader>
                <div className="flex flex-col gap-2">
                  <CommonInput
                    placeholder="키워드를 검색해보세요."
                    className="px-4 py-2.5 text-base w-full text-neutral-500 bg-brand-neutral-05 font-brand-neutral-60"
                    postfix={{
                      content: (
                        <div
                          onClick={() => {
                            searchCategoryList(keyword);
                          }}
                        >
                          <ZoomSearchIcon />
                        </div>
                      ),
                      className: "px-3",
                    }}
                    onChange={(text: string) => setKeyword(text)}
                    value={keyword}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        searchCategoryList(keyword);
                      }
                    }}
                  />
                  <div className="flex flex-col gap-2 pt-7">
                    <div>최근 이용한 서비스</div>
                    {!isArrayEmpty(
                      recentCategoryList.filter((item) =>
                        defaultCategoryList
                          .map((defaultItem) => defaultItem.serviceTemplateId)
                          .includes(item.serviceTemplateId)
                      )
                    ) ? (
                      <div className="flex gap-2.5 flex-wrap">
                        {recentCategoryList
                          .filter((item) =>
                            defaultCategoryList
                              .map(
                                (defaultItem) => defaultItem.serviceTemplateId
                              )
                              .includes(item.serviceTemplateId)
                          )
                          .slice(0, 5)
                          .map((category) => (
                            <CommonButton
                              variant="default2"
                              key={category.serviceTemplateId}
                              onClick={() => {
                                handleSelectCategoryWithStep(category);
                              }}
                            >
                              {category.serviceName}
                            </CommonButton>
                          ))}
                      </div>
                    ) : (
                      <div className="text-brand-neutral-60">
                        최근에 이용한 서비스가 없어요
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 pt-7">
                    <div>추천 서비스</div>
                    <div className="flex gap-2.5 flex-wrap">
                      {defaultCategoryList
                        .filter(
                          (item) =>
                            recentCategoryList.find(
                              (recentItem) =>
                                (item.serviceTemplateId &&
                                  recentItem.serviceTemplateId ===
                                    item.serviceTemplateId) ||
                                recentItem.serviceName === item.serviceName
                            ) === undefined
                        )
                        .slice(0, 10)
                        .map((category) => (
                          <CommonButton
                            variant="default2"
                            key={category.serviceTemplateId}
                            onClick={() => {
                              handleSelectCategoryWithStep(category);
                            }}
                          >
                            {category.serviceName}
                          </CommonButton>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                {search && (
                  <div className="pt-5">
                    {defaultCategoryList.length === 0 && (
                      <div className="sticky top-[72px] w-full h-40 flex flex-col items-center justify-center gap-2">
                        <NoDataIcon />
                        검색된 키워드가 없어요
                      </div>
                    )}
                    <div className="w-full flex flex-col items-center bg-brand-primary-gray-100 p-5 gap-4 text-white text-sm font-semibold">
                      찾는 키워드가 없나요?
                      <div className="grid grid-cols-2 gap-3 w-full">
                        <CommonButton
                          className="w-full rounded-lg"
                          variant="white"
                          onClick={() => {
                            if (
                              defaultCategoryList.find(
                                (item) => item.serviceName === "기타 문의"
                              ) === undefined
                            ) {
                              setPopup("invalidService");
                            } else {
                              const etcCategory = defaultCategoryList.find(
                                (item) => item.serviceName === "기타 문의"
                              );
                              if (etcCategory) {
                                handleSelectCategoryWithStep(etcCategory);
                              }
                            }
                          }}
                        >
                          <div className="flex flex-col">
                            <p className="text-base font-semibold">기타 문의</p>
                            <p className="text-xs text-brand-neutral-50">
                              소리 및 벌레 등 기타 불편
                            </p>
                          </div>
                        </CommonButton>
                        <CommonButton
                          className="w-full rounded-lg"
                          variant="white"
                          onClick={() => {
                            navigate(
                              `/myinfo/faq?backHistory=${location.pathname}`
                            );
                          }}
                        >
                          <div className="flex flex-col">
                            <p className="text-base font-semibold">FAQ</p>
                            <p className="text-xs text-brand-neutral-50">
                              자주 하는 질문
                            </p>
                          </div>
                        </CommonButton>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="flex flex-col">
              <CenterHeader>
                도와드리기 위해
                <br />
                <span className="font-medium">필요한 정보</span>가 있어요
              </CenterHeader>
              <div className="flex flex-col gap-7">
                <div className="flex flex-col gap-2">
                  <CommonLabel
                    label="요청 장소"
                    required={selectedCategory?.isRequiredVocPlace}
                  />
                  <CommonTextArea
                    placeholder={
                      selectedCategory?.vocPlacePlaceholder?.replace(
                        "요 ",
                        "요\n"
                      ) ?? "정확한 위치를 입력해주세요"
                    }
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                      const text = e?.target?.value;
                      removeError("location");
                      setIncommodityCreateRequest((prev) => ({
                        ...prev,
                        location: text,
                      }));
                    }}
                    error={errors["location"]}
                    ref={locationRef}
                    inputClassName="h-[75px]"
                  />
                </div>
                {requestTypeItems && requestTypeItems.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <CommonLabel label="상세 요청" required />
                    <RadioButtonGroup
                      variantActive="select"
                      items={requestTypeItems}
                      value={selectedRequestTypeItem as KeyValue}
                      onChange={(selectItem) => {
                        setSelectedRequestTypeItem(selectItem);
                      }}
                    />
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <CommonTextArea
                    label="상세 내용"
                    required={selectedCategory?.isRequiredVocDetail}
                    name="detail"
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                      removeError("description");
                      setIncommodityCreateRequest((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }));
                    }}
                    placeholder={"상세 내용을 입력해주세요"}
                    error={errors["description"]}
                    ref={descriptionRef}
                    inputClassName="h-[120px]"
                    subLabel={
                      <div className="whitespace-pre-line  text-brand-primary-magenta leading-[19px]">
                        {safeReplaceAll(
                          selectedCategory?.vocDetailPlaceholder ?? "",
                          "\\n",
                          "\n"
                        )}
                      </div>
                    }
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <CommonLabel label="이미지" />
                    <div className="text-xs text-brand-neutral-40">
                      1장 등록 가능
                    </div>
                  </div>
                  <CommonImageInput
                    maxImages={1}
                    images={images}
                    onChange={(images) => setImages(images)}
                    setIsLoading={setIsLoading}
                    title={"불편신고 이미지"}
                    type="center"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <CommonLabel label="일정 여부" />
                  <RadioButtonGroup
                    variantActive="select"
                    items={scheduleOption}
                    value={scheduleMode}
                    onChange={(value: KeyValue) => {
                      setScheduleMode(value);
                      setScheduleDatetime(undefined);
                    }}
                  />
                </div>
                {scheduleMode.key === "request" && (
                  <div>
                    <CommonLabel label="요청 날짜" />
                    <CommonDatetimeInput
                      ref={deadLineRef}
                      error={errors["deadLine"]}
                      value={scheduleDatetime}
                      minuteStep={10}
                      postfix={<span className="font-bold">이후</span>}
                      onClick={() => removeError("deadLine")}
                      onChange={(date) => {
                        removeError("deadLine");
                        handleDatetimeChange(date);
                      }}
                      className="w-full"
                      minDate={new Date()}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {step !== 1 ? (
        <div className="fixed bottom-0 bg-white w-full h-[50px]">
          <PrimaryButton
            validation={isDisabled()}
            className="text-white h-[50px] w-full flex justify-center items-center text-base font-medium leading-[19px]"
            onClick={handleConfirm}
          >
            확인
          </PrimaryButton>
        </div>
      ) : (
        <div className="fixed bottom-0  w-full flex justify-center items-center z-50 bg-white pt-2">
          <CategoryList
            buildingId={selectedBuilding?.buildingId ?? ""}
            onSelect={(category: IncommodityCategory) =>
              handleSelectCategoryWithStep(category)
            }
          />
        </div>
      )}

      <NeedCertCompanyPopup popup={popup} />
      <CommonPopup
        isOpen={popup === "confirmation"}
        onClose={() => setPopup("")}
        padding={0}
        title="접수 확인"
        unUseHorizonRule
      >
        <ConfirmReport
          incommodityCreate={incommodityCreateRequest}
          selectedCategory={selectedCategory}
          scheduleMode={scheduleMode.key}
          scheduleDatetime={scheduleDatetime}
          onClickPrev={() => setPopup("")}
          onClickNext={handleInsertIncommodity}
          selectedBuilding={selectedBuilding}
          selectedFloor={selectedFloor}
        />
      </CommonPopup>

      <SimplePopup
        isOpen={simplePopup === "reportFailed"}
        onClose={() => setSimplePopup("")}
        message="불편신고 접수에 실패했습니다."
      />

      <SimplePopup
        isOpen={popup === "invalidService"}
        onClose={() => setPopup("")}
        message="현재 지원되지 않는 서비스 입니다."
      />

      {simplePopupComponent}
    </div>
  );
}

export const NoDataIcon = () => (
  <svg
    width="60"
    height="60"
    viewBox="0 0 60 60"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M55.3997 55.3997L41.9805 41.9805M26.8282 48.2568C14.9936 48.2568 5.39966 38.6629 5.39966 26.8283C5.39966 14.9936 14.9936 5.39969 26.8282 5.39969C38.6629 5.39969 48.2568 14.9936 48.2568 26.8283C48.2568 38.6629 38.6629 48.2568 26.8282 48.2568Z"
      stroke="#1D1929"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M26.9108 17.3149V26.9923"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M26.9004 33.7988H26.9178"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);

export const CurrentPosition = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14.6475 7.75C14.6475 11.4332 11.6616 14.4191 7.97839 14.4191M14.6475 7.75C14.6475 4.06678 11.6616 1.08093 7.97839 1.08093M14.6475 7.75C13.3452 7.75 11.9802 7.75 11.9802 7.75M7.97839 14.4191C4.29517 14.4191 1.30933 11.4332 1.30933 7.75M7.97839 14.4191V11.7518M1.30933 7.75C1.30933 4.06678 4.29517 1.08093 7.97839 1.08093M1.30933 7.75H3.97659M7.97839 1.08093V3.7482"
      stroke="#9F9F9F"
      strokeWidth="1.5"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);
